import { t } from "i18next";
import { get, indexOf, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { setLoading } from "../../../redux/ducks/Loading";
import { useDispatch, useSelector } from "react-redux";
import { formatNumber, showError, showSuccess } from "../../../utils/utilities";
import { Checkbox, Col, Input, Modal, Row, Spin, Table } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import { useNavigate, useParams } from "react-router-dom";
import TimesheetPortal from "../../../services/api/TimesheetPortal";
import { setSection } from "../../../redux/ducks/Section";

const TimesheetApprovalDetailPM = () => {
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const projectId = useParams().id;
  const navigate = useNavigate();
  const [state, setState] = useState({
    tableData: [],
    dynamicForm: [],
    modal: false,
  });

  useEffect(() => {
    if (projectId) {
      dispatch(setSection({ section: { title: `${projectId}` } }));
    }
    getData();
  }, []);

  const getData = async () => {
    dispatch(setLoading(true));
    const table = await TimesheetPortal.getTimesheetApprovalProjects(projectId);
    if (!table.error) {
      const dynamicFormData = [];
      table.data.forEach((cc, i) => {
        cc.key = i;
        cc.users.forEach((user, j) => {
          user.key = `${i}-${j}`;
          user.projectTaskName = cc.projectTaskName;
          user.projectTaskId = cc.projectTaskId;
          user.monthWork = cc.monthWork;
          user.pmNotes = user.pmNotes || ""; // To handle null values
          dynamicFormData.push(user);
        });
      });
      setState((prevState) => ({
        ...prevState,
        tableData: table.data,
        dynamicForm: dynamicFormData,
      }));
    } else {
      showError(get(table, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleCheckbox = (event, data) => {
    let newData = state.dynamicForm.find((item) => item.key === data.key);
    newData.cid = event.target.checked;
    setState((prevState) => ({
      ...prevState,
      dynamicForm: [...prevState.dynamicForm.filter((item) => item.key !== data.key), newData],
    }));
  };

  const handleNotes = (event, data) => {
    let newData = state.dynamicForm.find((item) => item.key === data.key);
    newData.pmNotes = event.target.value;
    setState((prevState) => ({
      ...prevState,
      dynamicForm: [...prevState.dynamicForm.filter((item) => item.key !== data.key), newData],
    }));
  };

  const getNoteValue = (key) => {
    return state.dynamicForm.find((item) => item.key === key).pmNotes;
  };

  const expandedRowRender = (record) => {
    const columns = [
      { title: t("timesheet.approval.table.name"), dataIndex: "name", key: "name" },
      { title: t("timesheet.approval.table.surname"), dataIndex: "surname", key: "surname" },
      {
        title: t("timesheet.approval.table.hours"),
        dataIndex: "monthAmount",
        key: "monthAmount",
        render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
      },
      {
        title: t("timesheet.approval.table.pmNotes"),
        dataIndex: "pmNotes",
        key: "pmNotes",
        width: 200,
        render: (_, record) => (
          <div>
            <Input onChange={(event) => handleNotes(event, record)} value={getNoteValue(record.key)} />
          </div>
        ),
      },
      {
        title: t("timesheet.approval.table.cid"),
        dataIndex: "cid",
        key: "cid",
        width: 100,
        render: (_, record) => <Checkbox onChange={(event) => handleCheckbox(event, record)} defaultChecked />,
      },
    ];
    return (
      <Table columns={columns} dataSource={record.users} pagination={false} bordered scroll={true} size={"small"} />
    );
  };

  const columns = [
    { title: t("timesheet.approval.table.projectTaskId"), dataIndex: "projectTaskId", key: "projectTaskId" }, // Project
    { title: t("timesheet.approval.table.projectTaskName"), dataIndex: "projectTaskName", key: "projectTaskName" }, // Description activity
    { title: t("timesheet.approval.table.projectName"), dataIndex: "projectName", key: "projectName" }, // Description
    {
      title: t("timesheet.approval.table.monthAmount"),
      dataIndex: "monthAmount",
      key: "monthAmount",
      render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("timesheet.approval.table.monthWork"),
      dataIndex: "monthWork",
      key: "monthWork",
      render: (amount) => formatNumber({ value: amount, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
    },
  ];

  const submitApproval = async () => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const payload = [];
    state.dynamicForm.forEach((item) => {
      payload.push({
        timesheetPortalId: item.timesheetPortalId,
        pmNotes: item.pmNotes,
        status: item.cid ? "APPROVED" : "REFUTED",
      });
    });
    const submitResponse = await TimesheetPortal.submitTimesheetApproval(projectId, payload);
    if (!submitResponse.error) {
      showSuccess();
      closeModal();
      navigate("/timesheet-approval");
    } else {
      showError(get(submitResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const openModal = () => {
    setState((prevState) => ({ ...prevState, modal: true }));
  };

  const closeModal = () => {
    setState((prevState) => ({ ...prevState, modal: false }));
  };

  const summaryApprovedBlock = () => {
    let approved = [];
    state.dynamicForm?.forEach((row) => {
      if (row.cid) approved.push(row);
    });
    if (approved.length) {
      return (
        <>
          <Row>
            <Col span={24}>
              <div className="approved-list-title fw-bold">{t("timesheet.approval.modal.approved")}</div>
            </Col>
          </Row>
          <Row>
            <Col span={4}>
              <div className="fw-bold">{t("timesheet.approval.modal.project")}</div>
            </Col>
            <Col span={5}>
              <div className="fw-bold">{t("timesheet.approval.modal.activity")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.monthWork")}</div>
            </Col>
            <Col span={2}>
              <div className="fw-bold">{t("timesheet.approval.modal.hours")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.name")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.surname")}</div>
            </Col>
            <Col span={4}>
              <div className="fw-bold">{t("timesheet.approval.modal.pmNotes")}</div>
            </Col>
          </Row>
          {approved.map((timesheet) => {
            return (
              <Row key={timesheet.key}>
                <Col span={4}>{timesheet.projectTaskId}</Col>
                <Col span={5}>{timesheet.projectTaskName}</Col>
                <Col span={3}>{formatNumber({ value: timesheet.monthWork, lang: currentLanguage, suffix: "EUR" })}</Col>
                <Col span={2}>
                  {formatNumber({ value: timesheet.monthAmount, lang: currentLanguage, suffix: "HOUR" })}
                </Col>
                <Col span={3}>{timesheet.name}</Col>
                <Col span={3}>{timesheet.surname}</Col>
                <Col span={4}>{timesheet.pmNotes}</Col>
              </Row>
            );
          })}
        </>
      );
    }
  };

  const summaryRefusedBlock = () => {
    let refused = [];
    state.dynamicForm?.forEach((row) => {
      if (!row.cid) {
        refused.push(row);
      }
    });
    if (refused.length) {
      return (
        <>
          <Row>
            <Col span={24}>
              <div className="PFred fw-bold">{t("timesheet.approval.modal.refused")}</div>
            </Col>
          </Row>
          <Row>
            <Col span={4}>
              <div className="fw-bold">{t("timesheet.approval.modal.project")}</div>
            </Col>
            <Col span={5}>
              <div className="fw-bold">{t("timesheet.approval.modal.activity")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.monthWork")}</div>
            </Col>
            <Col span={2}>
              <div className="fw-bold">{t("timesheet.approval.modal.hours")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.name")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.surname")}</div>
            </Col>
            <Col span={4}>
              <div className="fw-bold">{t("timesheet.approval.modal.pmNotes")}</div>
            </Col>
          </Row>
          {refused.map((timesheet) => {
            return (
              <Row key={timesheet.key}>
                <Col span={4}>{timesheet.projectTaskId}</Col>
                <Col span={5}>{timesheet.projectTaskName}</Col>
                <Col span={3}>{formatNumber({ value: timesheet.monthWork, lang: currentLanguage, suffix: "EUR" })}</Col>
                <Col span={2}>
                  {formatNumber({ value: timesheet.monthAmount, lang: currentLanguage, suffix: "HOUR" })}
                </Col>
                <Col span={3}>{timesheet.name}</Col>
                <Col span={3}>{timesheet.surname}</Col>
                <Col span={4}>{timesheet.pmNotes}</Col>
              </Row>
            );
          })}
        </>
      );
    }
  };

  const disableConfirm = () => {
    let check = true;
    state.dynamicForm.forEach((df) => {
      if (!df.cid && indexOf([null, undefined, ""], df.pmNotes) !== -1) {
        check = false;
      }
    });
    return !check;
  };

  return (
    <>
      {!isEmpty(state.tableData) && (
        <Table
          columns={columns}
          dataSource={state.tableData}
          defaultExpandAllRows
          sticky
          bordered
          expandable={{
            expandedRowRender,
            expandIcon: () => null,
            expandIconColumnIndex: -1,
          }}
          scroll={true}
          pagination={false}
          size={"middle"}
        />
      )}
      <Row type="flex" align="middle" justify="end" className="mt-10">
        <Button
          currentIcon={<CheckOutlined className="icon-medium" />}
          clickCallback={openModal}
          isDisabled={disableConfirm()}
        />
      </Row>
      <Modal
        title={t("timesheet.approval.modal.title")}
        open={state.modal}
        width={900}
        onOk={closeModal}
        onCancel={closeModal}
        footer={[
          <Button
            key="timesheet-approval"
            currentIcon={<CheckOutlined className="icon-medium" />}
            clickCallback={() => submitApproval()}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        {summaryApprovedBlock()}
        {summaryRefusedBlock()}
      </Modal>
    </>
  );
};

export default TimesheetApprovalDetailPM;
